import { Suspense, lazy, useState, type ReactElement } from 'react';
import { standInTrigger, useIdlePreload } from '../../utils/deferred';
import type { ConfirmDialogProps, DialogBaseProps, DialogPlacement } from './DialogPopup';

export type { ConfirmDialogProps, DialogPlacement } from './DialogPopup';

const load = () => import('./DialogPopup');
const DialogShell = lazy(() => load().then((m) => ({ default: m.DialogShell })));
const ConfirmDialogPopup = lazy(() => load().then((m) => ({ default: m.ConfirmDialog })));

/**
 * Until a dialog is first opened, only its trigger is on the page; see utils/deferred. Controlled
 * dialogs (`open`) mount as soon as they are opened; a press on the stand-in trigger opens them
 * through onOpenChange, uncontrolled ones through their own state.
 */
function useDeferredDialog(props: {
  trigger?: ReactElement;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [opened, setOpened] = useState(false);
  useIdlePreload(load);
  const controlled = props.open !== undefined;
  const standIn = props.trigger
    ? standInTrigger(
        props.trigger,
        load,
        () => {
          setOpened(true);
          if (controlled) props.onOpenChange?.(true);
        },
        'dialog',
      )
    : null;
  const mounted = opened || props.open === true || props.defaultOpen === true;
  const defaultOpen = controlled ? undefined : (props.defaultOpen ?? opened);
  return { standIn, mounted, defaultOpen };
}

function Deferred(props: DialogBaseProps & { placement: DialogPlacement }) {
  const { standIn, mounted, defaultOpen } = useDeferredDialog(props);
  if (!mounted) return standIn;
  return (
    <Suspense fallback={standIn}>
      <DialogShell {...props} defaultOpen={defaultOpen} />
    </Suspense>
  );
}

/** Centred modal dialog (§23.2 Modal). */
export function Modal(props: DialogBaseProps) {
  return <Deferred placement="center" {...props} />;
}

/** Bottom sheet on phones, centred modal from `sm` up (§23.2 Sheet, §23.1 250 ms transitions). */
export function Sheet(props: DialogBaseProps) {
  return <Deferred placement="bottom" {...props} />;
}

/** Side panel from the inline end, so it opens from the left in Arabic (§23.2 Drawer). */
export function Drawer(props: DialogBaseProps) {
  return <Deferred placement="end" {...props} />;
}

/** Destructive confirmation that only enables once the user types `confirmText` exactly. */
export function ConfirmDialog(props: ConfirmDialogProps) {
  const { standIn, mounted, defaultOpen } = useDeferredDialog(props);
  if (!mounted) return standIn;
  return (
    <Suspense fallback={standIn}>
      <ConfirmDialogPopup {...props} defaultOpen={defaultOpen} />
    </Suspense>
  );
}
